/* eslint-disable react/button-has-type */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import Button from 'common/Button';
import Spinner from 'common/Spinner';
import { withStyles } from 'theme/withStyles';
import backButtonStyles from 'groups/styles/backButton';
import buttonRowStyles from 'groups/styles/buttonRow';
import uiActions from 'core/ui/actions';
import groupActions from 'core/groups/actions';
import hasError from 'utils/hasError';
import pick from 'lodash/pick';
import pageActions from 'core/page/actions';

const styles = (theme) => ({
  back: backButtonStyles(theme),
  button: {
    margin: [0, 0, theme.spacing.xs, 0],
    [`@media(min-width: ${theme.screenSizes.mobileMax}px)`]: {
      margin: [0, 'auto', theme.spacing.xs],
      width: 300,
    },
  },
  forceHide: {
    display: 'none !important',
  },
  forceShow: {},
  root: {
    ...buttonRowStyles(theme),
    '&$forceShow': {
      display: 'flex',
    },
  },
});

const groupsForceShowButtons = [
  'offers_declined_with_crosssale',
  'business_users',
  'prod_start',
  'signerstatus',
];

export const ButtonRowGroup = ({
  backAllowed,
  backButtonLabel,
  classes,
  continueAllowed,
  disableButton,
  groupName,
  isLaunch,
  loading,
  onBack,
  onContinueClick,
  onSave,
  pageName,
  saveAndContinueAllowed,
  saveButtonLabel,
  setActiveGroup,
  submitButtonLabel,
}) => {
  const name = groupName.toLowerCase();

  return (
    <div
      className={classnames('row btn-row-group', `btn-group-${name}`, classes.root, {
        [classes.forceShow]: groupsForceShowButtons.includes(name) || isLaunch,
        [classes.forceHide]: pageName === 'productoptions' || pageName === 'beneficiaries',
      })}
    >
      {continueAllowed && (
        <Button
          className={classnames(
            'btn btn-mobile btn-grp-next',
            `btn-next-${name}`,
            'mt-5',
            classes.button,
            {
              loading,
            },
          )}
          data-name="btn-grp-next"
          onClick={onContinueClick}
          disabled={disableButton}
        >
          <RawHtml type="span">{submitButtonLabel}</RawHtml>{' '}
          <i className="icon-arrow-thin-right" aria-hidden="true" />
          <Spinner loading={loading} />
        </Button>
      )}
      {saveAndContinueAllowed && (
        <RawHtml
          className={classnames(
            'btn btn-mobile btn-tertiary btn-grp-save-and-continue',
            classes.button,
          )}
          data-name="btn-grp-save-and-continue"
          type="button"
          disabled={disableButton}
          onClick={onSave}
          variant={!continueAllowed && name === 'business_users' ? 'primary' : 'secondary'}
        >
          {saveButtonLabel}
        </RawHtml>
      )}

      {(backAllowed || isLaunch) && (
        <RawHtml
          className={classnames(
            'btn btn-mobile btn-tertiary btn-grp-back mt-5',
            classes.button,
            classes.back,
          )}
          data-name="btn-grp-back"
          type="button"
          onClick={isLaunch ? setActiveGroup : onBack}
          variant="secondary"
          disabled={loading}
        >
          {backButtonLabel}
        </RawHtml>
      )}
    </div>
  );
};

ButtonRowGroup.propTypes = {
  backAllowed: PropTypes.bool,
  backButtonLabel: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  continueAllowed: PropTypes.bool,
  disableButton: PropTypes.bool,
  groupName: PropTypes.string.isRequired,
  isLaunch: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onContinueClick: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  pageName: PropTypes.string,
  saveAndContinueAllowed: PropTypes.bool,
  saveButtonLabel: PropTypes.string,
  setActiveGroup: PropTypes.func.isRequired,
  submitButtonLabel: PropTypes.string,
};

ButtonRowGroup.defaultProps = {
  backAllowed: false,
  backButtonLabel: '',
  continueAllowed: true,
  disableButton: false,
  pageName: '',
  saveAndContinueAllowed: false,
  saveButtonLabel: '',
  submitButtonLabel: '',
};

const ButtonRowGroupStyled = withStyles(styles)(ButtonRowGroup);

const mapStateToProps = ({ data, fields, groups, page, ui }, { groupName }) => {
  const group = groups[groupName];
  const loading = !!(ui.groupLoading || ui.fieldLoading || ui.appLoading || ui.pageLoading);
  const isProdStartInvalid = groupName === 'PROD_START' && !data.productLookup;
  return {
    backAllowed:
      groupName !== 'FUNDING_TRANSFER_EXT' &&
      groupName !== 'FUNDING_TRANSFER_INT' &&
      (page.backAllowed || Object.keys(groups)[0] !== groupName),
    backButtonLabel: group.backButtonLabel,
    continueAllowed: page.continueAllowed,
    disableButton:
      loading || hasError(pick(fields, group.fields)) || ui.menuOpen || isProdStartInvalid || false,
    isLaunch: page.name === 'launch',
    loading,
    pageName: page.name,
    saveAndContinueAllowed: page.saveAndContinueAllowed,
    saveButtonLabel: page.saveButtonLabel,
    submitButtonLabel: group.submitButtonLabel,
  };
};

const mapDispatchToProps = (dispatch, { groupName }) => ({
  onBack: () => {
    if (groupName.startsWith('DONE')) {
      dispatch(pageActions.logout());
    } else if (groupName === 'FUNDING_CC') {
      dispatch(uiActions.setActiveGroup('FUNDING_AMOUNT'));
    } else {
      dispatch(groupActions.back());
    }
  },
  onContinueClick: () => {
    /** HACK: This should be removed when the prod start page
     * is replaced with the product showcase
     */
    if (groupName === 'PROD_START') {
      dispatch(groupActions.prodStartNext());
      return;
    }
    if (groupName === 'FUNDING_EXT_VERIFICATION') {
      groupActions.apiAction('FUNDING_TRANSFER_EXT/actions/verifyQuestions');
    } else {
      dispatch(groupActions.next(groupName));
    }
  },
  onSave: () => {
    dispatch(groupActions.save(groupName));
  },
  setActiveGroup: () => {
    dispatch(uiActions.setActiveGroup(''));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ButtonRowGroupStyled);
